import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';

import type { MaxWidthTypes } from '../../utilities/shared/MaxWidth';
import type { MinWidthTypes } from '../../utilities/shared/MinWidth';
import type { WidthTypes } from '../../utilities/shared/Width';
import { zIndexes } from '../../common/z_indexes';

export type ShellPropSize = 'x-small' | 'small' | 'medium';

type ShellSize = ShellPropSize;
const ShellSizeContext = createContext<ShellSize | undefined>(undefined);
export const ShellSizeProvider = ShellSizeContext.Provider;
export const useShellSize = (controlledValue?: ShellSize, defaultValue: ShellSize = 'x-small') => {
  const shellSize = useContext(ShellSizeContext);
  return controlledValue ?? shellSize ?? defaultValue;
};

export type ShellPropLayoutMode = 'compact' | 'detailed';

type ShellLayoutMode = ShellPropLayoutMode;
const ShellLayoutModeContext = createContext<ShellLayoutMode | undefined>(undefined);
export const ShellLayoutModeProvider = ShellLayoutModeContext.Provider;
export const useShellLayoutMode = (
  controlledValue?: ShellLayoutMode,
  defaultValue: ShellLayoutMode = 'compact',
) => {
  const shellLayoutMode = useContext(ShellLayoutModeContext);
  return controlledValue ?? shellLayoutMode ?? defaultValue;
};

export type ShellPropContentMode = 'fit' | 'stretch';

type ShellContentMode = ShellPropContentMode;
const ShellContentModeContext = createContext<ShellContentMode | undefined>(undefined);
export const ShellContentModeProvider = ShellContentModeContext.Provider;
export const useShellContentMode = (
  controlledValue?: ShellContentMode,
  defaultValue: ShellContentMode = 'stretch',
) => {
  const shellContentMode = useContext(ShellContentModeContext);
  return controlledValue ?? shellContentMode ?? defaultValue;
};

export type ShellPropWidth = WidthTypes;

type ShellWidth = ShellPropWidth;
const ShellWidthContext = createContext<ShellWidth | undefined>(undefined);
export const ShellWidthProvider = ShellWidthContext.Provider;
export const useShellWidth = (controlledValue?: ShellWidth, defaultValue: ShellWidth = 'full') => {
  const shellWidth = useContext(ShellWidthContext);
  return controlledValue ?? shellWidth ?? defaultValue;
};

export type ShellPropWidthMode = 'centered' | 'full';

type ShellWidthMode = ShellPropWidthMode;
const ShellWidthModeContext = createContext<ShellWidthMode | undefined>(undefined);
export const ShellWidthModeProvider = ShellWidthModeContext.Provider;
export const useShellWidthMode = (
  controlledValue?: ShellWidthMode,
  defaultValue: ShellWidthMode = 'full',
) => {
  const shellWidthMode = useContext(ShellWidthModeContext);
  return controlledValue ?? shellWidthMode ?? defaultValue;
};

type ShellMaxWidth = MaxWidthTypes;
const ShellMaxWidthContext = createContext<ShellMaxWidth | undefined>(undefined);
export const ShellMaxWidthProvider = ShellMaxWidthContext.Provider;
export const useShellMaxWidth = (
  controlledValue?: ShellMaxWidth,
  defaultValue: ShellMaxWidth = 'full',
) => {
  const shellMaxWidth = useContext(ShellMaxWidthContext);
  return controlledValue ?? shellMaxWidth ?? defaultValue;
};

type ShellMinWidth = MinWidthTypes;
const ShellMinWidthContext = createContext<ShellMinWidth | undefined>(undefined);
export const ShellMinWidthProvider = ShellMinWidthContext.Provider;
export const useShellMinWidth = (
  controlledValue?: ShellMinWidth,
  defaultValue: ShellMinWidth = 'full',
) => {
  const shellMinWidth = useContext(ShellMinWidthContext);
  return controlledValue ?? shellMinWidth ?? defaultValue;
};

export type ShellPropType = 'dialog' | 'drawer' | 'pane';

type ShellType = ShellPropType;
const ShellTypeContext = createContext<ShellType | undefined>(undefined);
export const ShellTypeProvider = ShellTypeContext.Provider;
export const useShellType = (controlledValue?: ShellType, defaultValue: ShellType = 'pane') => {
  const shellType = useContext(ShellTypeContext);
  return controlledValue ?? shellType ?? defaultValue;
};

export interface ShellLayerElement {
  id: string;
  position: 'top' | 'bottom';
  distance: number; // Distance from viewport edge
  zIndex: number;
}

export interface UseShellLayerManagerReturn {
  /**
   * Register a sticky element and get its z-index
   */
  registerElement: (id: string, position: 'top' | 'bottom', distance: number) => number;
  /**
   * Unregister a sticky element
   */
  unregisterElement: (id: string) => void;
  /**
   * Update the distance of a registered element
   */
  updateElementDistance: (id: string, distance: number) => void;
  /**
   * Get the current z-index for an element
   */
  getElementZIndex: (id: string) => number | undefined;
}

const BASE_Z_INDEX = zIndexes.content;
const Z_INDEX_STEP = 1;

export function ShellLayerManager(): UseShellLayerManagerReturn {
  const elementsRef = useRef<Map<string, ShellLayerElement>>(new Map());

  const recalculateZIndices = useCallback(() => {
    const elementsArray = Array.from(elementsRef.current.values());

    // Separate elements by position
    const topElements = elementsArray
      .filter((el) => el.position === 'top')
      .sort((a, b) => b.distance - a.distance); // Farthest first (highest distance)

    const bottomElements = elementsArray
      .filter((el) => el.position === 'bottom')
      .sort((a, b) => a.distance - b.distance); // Closest first (lowest distance)

    // Assign z-indices directly to the ref
    const updatedElements = new Map<string, ShellLayerElement>();

    // Top elements: farthest gets lowest z-index
    topElements.forEach((element, index) => {
      updatedElements.set(element.id, {
        ...element,
        zIndex: BASE_Z_INDEX + index * Z_INDEX_STEP,
      });
    });

    // Bottom elements: closest gets highest z-index
    bottomElements.forEach((element, index) => {
      updatedElements.set(element.id, {
        ...element,
        zIndex: BASE_Z_INDEX + (topElements.length + index) * Z_INDEX_STEP,
      });
    });

    // Update only the ref, no state changes
    elementsRef.current = updatedElements;
  }, []);

  // Store the function in a ref to avoid dependency cycles
  const recalculateRef = useRef(recalculateZIndices);
  recalculateRef.current = recalculateZIndices;

  const registerElement = useCallback(
    (id: string, position: 'top' | 'bottom', distance: number): number => {
      const newElement: ShellLayerElement = {
        id,
        position,
        distance,
        zIndex: BASE_Z_INDEX, // Temporary z-index
      };

      // Update ref immediately
      elementsRef.current.set(id, newElement);

      // Recalculate z-indices after adding the new element
      recalculateRef.current();

      // Get the updated z-index from the ref
      return elementsRef.current.get(id)?.zIndex || BASE_Z_INDEX;
    },
    [],
  );

  const unregisterElement = useCallback((id: string) => {
    // Remove from ref immediately
    elementsRef.current.delete(id);

    // Recalculate z-indices after removing the element
    recalculateRef.current();
  }, []);

  const updateElementDistance = useCallback((id: string, distance: number) => {
    const element = elementsRef.current.get(id);
    if (!element) return;

    // Update ref immediately
    elementsRef.current.set(id, { ...element, distance });

    // Recalculate z-indices after updating distance
    recalculateRef.current();
  }, []);

  const getElementZIndex = useCallback(
    (id: string): number | undefined => elementsRef.current.get(id)?.zIndex,
    [],
  );

  return {
    registerElement,
    unregisterElement,
    updateElementDistance,
    getElementZIndex,
  };
}

const ShellLayerScrollContext = createContext<{
  layerManager: UseShellLayerManagerReturn | null;
  scrollContainer: HTMLElement | null;
}>({ layerManager: null, scrollContainer: null });

export function ShellLayerScrollProvider({
  children,
  scrollContainer,
}: {
  children: React.ReactNode;
  scrollContainer: HTMLElement | null;
}) {
  const layerManager = ShellLayerManager();

  const contextValue = useMemo(
    () => ({ layerManager, scrollContainer }),
    [layerManager, scrollContainer],
  );

  return (
    <ShellLayerScrollContext.Provider value={contextValue}>
      {children}
    </ShellLayerScrollContext.Provider>
  );
}

export function useShellLayerScrollContext(): {
  layerManager: UseShellLayerManagerReturn;
  scrollContainer: HTMLElement | null;
} {
  const { layerManager, scrollContainer } = useContext(ShellLayerScrollContext);
  if (!layerManager) {
    throw new Error('useShellLayerScrollContext must be used within a ShellLayerScrollProvider');
  }
  return { layerManager, scrollContainer };
}
